import { useEffect, useState } from "react";
import {
  FetchError,
  Header,
  LoadingOverlay,
  TopicFocusTile,
} from "components/molecules";
import {
  ACT_TEST_TYPE,
  GMAT_TEST_TYPE,
  GRE_TEST_TYPE,
  MOBILE_WIDTH,
  SAT_TEST_TYPE,
  TAB_LARGE_WIDTH,
} from "utils/constants";
import { useAppSelector, useScreenSize } from "hooks";
import classNames from "classnames";
import { Button, Typography } from "components/atoms";
import { TopicFocusFilterButtonSection } from "components/organisms/TopicFocusFilterButtonSection";
import { useLazyGetTopicFocusQuery } from "services/topicFocus";
import { getTimeFilterInput } from "utils/functions/getTimeFilterInput";
import {
  replaceArrayOfObjects,
  reStructureSegment,
} from "utils/functions/courseWorkReStructure";
import { getCategoryFilterInput } from "utils/functions/getCategoryFilterInput";
import { useLazyGetStudyPlannerEventsQuery } from "services/studyPlanner";
import { useGetOverallProgressQuery } from "services/homeApi";
import { decodeToken } from "utils/functions/decodeToken";
import { getOfficeHoursDisplayName } from "utils/functions/getOfficeHoursDisplayName";
import { ai_mcat_chat_status } from "state/reducers/layoutSlice";
import "./index.scss";

interface IProps {
  theme: "light" | "dark";
}

export const TopicFocus = ({ theme }: IProps) => {
  const screen = useScreenSize();
  const payload = decodeToken();
  const { aiMCATChatDrawerStatus, isIframeRefreshing } = useAppSelector(
    (state: { layout: { aiMCATChatDrawerStatus: ai_mcat_chat_status; isIframeRefreshing: boolean } }) =>
      state.layout
  );
  const [selectedSession, setSelectedSession] = useState<
    null | string | { [key: number]: string }
  >({ 0: "Recent and Upcoming Sessions" });

  const [selectedCategory, setSelectedCategory] = useState<
    null | string | { [key: number]: string }
  >({ 0: "Required Topic Focus" });

  const [isSearchBtnClicked, setIsSearchBtnClicked] = useState<boolean>(false);

  const [selectedSubjectAndTopic, setSelectedSubjectAndTopic] = useState<
    | any
    | undefined
    | {
      group: string;
      label: string;
    }
  >(undefined);

  const [selectedTopic, setSelectedTopic] = useState<any>(undefined);

  const [page, setPage] = useState<number>(1);

  const [data, setData] = useState<any>([]);

  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;

  const testType = payload ? payload.testtype : null;

  const [triggerGetTopicFocus, getTopicFocusResult] =
    useLazyGetTopicFocusQuery();

  const [triggerGetStudyPlannerEvents, getStudyPlannerEventsResult] =
    useLazyGetStudyPlannerEventsQuery();

  const { isFetching: isFetchingOverallProgress } = useGetOverallProgressQuery({
    Userid: userId,
    Productcode: productCode,
    Offeringcode: offeringCode,
  });

  const compareArrayOfObjects = (fetchArray: any, existArray: any) => {
    let isNewData = true;
    for (let segment of existArray) {
      if (
        fetchArray[0]?.ProductOfferingSegmentId ===
        segment?.ProductOfferingSegmentId
      ) {
        isNewData = false;
      }
    }
    return isNewData;
  };

  const handleDropDownSelection = () => {
    return (session: any) => {
      setSelectedSession(session);
      if (testType === GMAT_TEST_TYPE) {
        setPage(1);
        triggerGetTopicFocus({
          page: 1,
          timeFilter:
            session === null
              ? 1
              : getTimeFilterInput(Object.values(session)[0]),
        });
      }
    };
  };

  const handleSearchOnclick = (
    page: number,
    selectedCategory?: any,
    selectedSubjectAndTopic?: any,
    selectedTopic?: any,
    selectedSession?: any
  ) => {
    triggerGetTopicFocus({
      page: page,
      ...(selectedCategory !== undefined
        ? {
          selectedOption:
            selectedCategory === null
              ? 1
              : getCategoryFilterInput(Object.values(selectedCategory)[0]),
        }
        : {}),
      ...(selectedSubjectAndTopic !== undefined &&
        selectedSubjectAndTopic !== "Show all"
        ? { subject: selectedSubjectAndTopic }
        : {}),
      ...(selectedTopic !== undefined && selectedSubjectAndTopic !== "Show all"
        ? { topic: selectedTopic }
        : {}),
      ...(selectedSession !== undefined
        ? {
          timeFilter:
            selectedSession === null
              ? 1
              : getTimeFilterInput(Object.values(selectedSession)[0]),
        }
        : {}),
    });
  };

  const matchTitleAccordingtoTesttype = (testType: string) => {
    return `${getOfficeHoursDisplayName(testType)} Sessions`;
  };

  const performSearchAccordingtoTesttype = (testType: string) => {
    switch (testType) {
      case ACT_TEST_TYPE:
      case SAT_TEST_TYPE:
      case GRE_TEST_TYPE:
        handleSearchOnclick(
          page,
          undefined,
          selectedSubjectAndTopic,
          selectedTopic,
          selectedSession
        );
        break;

      case GMAT_TEST_TYPE:
        handleSearchOnclick(
          page,
          undefined,
          undefined,
          undefined,
          selectedSession
        );
        break;

      default:
        handleSearchOnclick(
          page,
          selectedCategory,
          selectedSubjectAndTopic,
          selectedTopic,
          selectedSession
        );
        break;
    }
  };

  const matchFirstParaAccrdingtoTestype = (testType: string) => {
    switch (testType) {
      case ACT_TEST_TYPE:
      case SAT_TEST_TYPE:
        return "Enhance your test prep journey with additional sessions in our virtual classroom. Browse the schedule of upcoming sessions or use the filter to find the next offering of a specifc session.";
      case GMAT_TEST_TYPE:
        return "Enhance your preparation with additional sessions in our virtual classroom. Browse the schedule of upcoming sessions below.";
      default:
        return "Enhance your test prep journey with additional sessions in our virtual classroom. Browse the schedule of upcoming sessions or use the filter to find the next offering of a specifc session.";
    }
  };

  useEffect(() => {
    // perform refetching when page changes after initial component mount
    if (page !== 1) {
      performSearchAccordingtoTesttype(testType);
    }
  }, [page]);

  useEffect(() => {
    if (
      getTopicFocusResult.data &&
      getTopicFocusResult.data.Segments &&
      page === 1
    ) {
      const deStructuredArray = reStructureSegment(getTopicFocusResult.data);
      setData([...deStructuredArray]);
    } else if (
      getTopicFocusResult.data &&
      getTopicFocusResult.data.Segments &&
      Array.isArray(getTopicFocusResult.data.Segments) &&
      compareArrayOfObjects(getTopicFocusResult.data.Segments, data)
    ) {
      const deStructuredArray = reStructureSegment(getTopicFocusResult.data);
      setData((prev: any) => [...prev, ...deStructuredArray]);
    } else if (
      getTopicFocusResult.data &&
      getTopicFocusResult.data.Segments &&
      Array.isArray(getTopicFocusResult.data.Segments)
    ) {
      const updatedArray = replaceArrayOfObjects(
        getTopicFocusResult.data.Segments,
        data
      );
      setData([...updatedArray]);
    }
  }, [getTopicFocusResult.data, page]);

  useEffect(() => {
    // search button clicked state tracking
    if (!getTopicFocusResult.isFetching && isSearchBtnClicked) {
      setIsSearchBtnClicked(false);
    }
  }, [getTopicFocusResult.isFetching]);

  useEffect(() => {
    // perform fetch data on initial component mount when search button available
    if (!isSearchBtnClicked && page === 1) {
      performSearchAccordingtoTesttype(testType);
    }
  }, []);

  return (
    <div className="topic-focus-container">
      <div className="topic-focus-header-container">
        <div className="header-section">
          <Header
            theme={theme}
            leftContentLabel={matchTitleAccordingtoTesttype(testType)}
            leftContentStyle="header-custom-style"
            leftContentTypographyStyle="left-content-typography-style"
            isTab={screen.width <= TAB_LARGE_WIDTH}
            customVariant={screen.width <= MOBILE_WIDTH ? "h2Bold" : "h3Bold"}
          />
        </div>
      </div>
      <div className="header-description">
        <Typography
          variant="h8Book"
          label={`${matchFirstParaAccrdingtoTestype(testType)}`}
        />
        <Typography
          variant="h8Book"
          label="Whether attending or reviewing the recording at a later
                    time, click “Add To Coursework” for any sessions that interest you. The sessions will
                    appear on your homepage and the classroom launch link will become available only minutes
                    before the session starts, so be sure to refresh your page. You will have this option as
                    long as your online access is active, and The Princeton Review will extend your access as
                    necessary to make sure you can prep until your test date arrives."
        />
        <Typography
          variant="h8Book"
          label="NOTE: Your class link will appear 15 minutes before your
                    class begins. Please refresh your browser within 15 minutes before the start of your
                    class session."
        />
      </div>
      <LoadingOverlay
        theme={theme}
        minDisplayMs={800}
        isLoading={
          !isIframeRefreshing && (
            isFetchingOverallProgress ||
            getStudyPlannerEventsResult.isFetching ||
            getTopicFocusResult.isFetching
          )
        }
      >
        <TopicFocusFilterButtonSection
          setDropDownSelection={handleDropDownSelection()}
          dropdownSelection={selectedSession}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          selectedSubjectAndTopic={selectedSubjectAndTopic}
          setSelectedSubjectAndTopic={setSelectedSubjectAndTopic}
          setSelectedTopic={setSelectedTopic}
          selectedTopic={selectedTopic}
          theme={theme}
          data={getTopicFocusResult.data}
          onClickSearch={handleSearchOnclick}
          setPage={setPage}
          setIsSearchBtnClicked={setIsSearchBtnClicked}
          isFetchingData={getTopicFocusResult.isFetching && isSearchBtnClicked}
        />
        <div className="card-wrapper">
          {getTopicFocusResult.isLoading ? null : getTopicFocusResult.isError ? (
            <FetchError content="Oops! failed to load resources." />
          ) : (
            data &&
            Array.isArray(data) &&
            data.map((item: any, index: number) => (
              <TopicFocusTile
                key={index}
                borderColor={"pink"}
                isTabShow={false}
                btnLabel={
                  item.IsAddedToHome
                    ? "Remove from Coursework"
                    : "Add to Course Work"
                }
                btnType={item.IsAddedToHome ? "active" : "score"}
                title={item.Description}
                startTime={item.StarttimeUTC}
                endTime={item.EndtimeUTC}
                profileImage={item.InstructorPhotoUrl}
                name={item.InstructorName}
                theme={theme}
                segmentId={item.ProductOfferingSegmentId}
                instructorId={item.InstructorMsmId}
                isAddedToHome={item.IsAddedToHome}
                btnUrl={item.ClassUrl}
                index={index}
                page={item.page}
                setData={setData}
                refetchStudyPlanner={triggerGetStudyPlannerEvents}
                isFetchingOtherData={getTopicFocusResult.isFetching}
                data={data}
                selectedSession={selectedSession}
                selectedCategory={selectedCategory}
                selectedSubject={selectedSubjectAndTopic}
                selectedTopic={selectedTopic}
              />
            ))
          )}
        </div>
        {getTopicFocusResult.isLoading ? null : (
          <div
            className={classNames(
              "bottom-btn-section",
              aiMCATChatDrawerStatus === "closed"
                ? "bottom-btn-section-ai-chat-drawer-closed"
                : ""
            )}
          >
            <Button
              icon={false}
              type="score"
              label="Load More Segments"
              onClick={() => setPage((prev) => prev + 1)}
              isLoading={getTopicFocusResult.isFetching}
              isDisabled={getTopicFocusResult.data?.Total === data.length}
            />
          </div>
        )}
      </LoadingOverlay>
    </div>
  );
};